/**
 * NEW ANNOTATION CLIENT ARCHITECTURE
 * 
 * This file shows how to refactor the client to use the new server-side orchestration.
 * 
 * CHANGE SUMMARY:
 * - OLD: Client orchestrated steps sequentially (handleAnnotationRun with runLongRouteStep)
 * - NEW: Client sends ONE request with all parameters, then polls status only
 * 
 * BENEFITS:
 * ✓ Client disconnection doesn't interrupt pipeline
 * ✓ Server owns all orchestration logic (single source of truth)
 * ✓ No dependency on client connectivity for step sequencing
 * ✓ Simpler client code (just polling, no step launching)
 * ✓ Flexible: same route handles any tool combination
 */

import axios from 'axios';
import CONFIG from '../config';


/**
 * SIMPLIFIED: New annotation run flow using server-side orchestration
 */
export async function handleAnnotationRunNewArchitecture(runId, user, fetchUserAnnotations) {
    console.log('[NEW-ORCHESTRATION] Starting annotation run via new architecture');
    
    // Fetch run to get parameters
    const getRunResult = await axios.post(`${CONFIG.API_BASE_URL}/get_run`, { 'run_id': runId });
    if (getRunResult.status !== 200) {
        console.error('Error fetching run data');
        return;
    }
    
    const runData = getRunResult.data.data;
    const parameters = runData.parameters;
    const cpus = runData.parameters?.cpus || runData.cpus || 4;
    
    console.log('[NEW-ORCHESTRATION] Launching server-side orchestration with parameters:', parameters);
    
    // SINGLE REQUEST: Launch orchestration on server
    // The server handles ALL step sequencing, execution, and error handling
    try {
        const orchestrationResponse = await axios.post(
            `${CONFIG.API_BASE_URL}/run_annotation_orchestrated`,
            {
                run_id: runId,
                user: user,
                parameters: parameters,
                cpus: cpus
            }
        );
        
        if (orchestrationResponse.status !== 200) {
            console.error('Failed to start orchestration');
            return;
        }
        
        console.log('[NEW-ORCHESTRATION] Orchestration started on server, polling for progress...');
    } catch (error) {
        console.error('Error launching orchestration:', error);
        return;
    }
    
    // POLLING LOOP: Client becomes read-only observer
    // Poll every 30s to check status and update UI
    await pollAnnotationProgress(user, runId, fetchUserAnnotations);
}


/**
 * SIMPLIFIED: Polling loop - just check status every 30s
 * 
 * This replaces the complex runAsyncPolledStep() and handleAnnotationRun() logic.
 * All orchestration is now server-side; client just observes and updates UI.
 */
export async function pollAnnotationProgress(user, runId, fetchUserAnnotations, pollIntervalMs = 5000) {
    const maxPollAttempts = 1000; // ~8 hours at 30s intervals
    let pollCount = 0;
    
    return new Promise((resolve) => {
        const pollOnce = async () => {
            pollCount++;
            
            try {
                // Fetch current run state
                const response = await axios.post(`${CONFIG.API_BASE_URL}/get_run`, { 'run_id': runId });
                
                if (response.status !== 200) {
                    console.error('[POLLING] Failed to fetch run status');
                    return;
                }
                
                const runData = response.data.data;
                const status = runData.status;
                const progress = runData.progress;
                
                console.log(`[POLLING] Poll #${pollCount}: status=${status}, progress="${progress}"`);
                
                await fetchUserAnnotations(user, false);
                
                // Check if annotation is complete or failed
                if (status === 'completed') {
                    console.log('[POLLING] Annotation completed!');
                    resolve({ success: true, status: 'completed' });
                    return true;
                } else if (status === 'failed') {
                    const error = runData.error || 'Unknown error';
                    console.error('[POLLING] Annotation failed:', error);
                    resolve({ success: false, status: 'failed', error });
                    return true;
                } else if (pollCount >= maxPollAttempts) {
                    console.warn('[POLLING] Max poll attempts reached, stopping');
                    resolve({ success: false, status: 'timeout' });
                    return true;
                }
                // Otherwise continue polling...
                return false;
                
            } catch (error) {
                console.error('[POLLING] Error polling status:', error);
                // Continue polling despite errors
                return false;
            }
        };

        // Run one poll immediately to avoid a long blank initial state.
        pollOnce().then((isDone) => {
            if (isDone) return;

            const pollInterval = setInterval(async () => {
                const done = await pollOnce();
                if (done) {
                    clearInterval(pollInterval);
                }
            }, pollIntervalMs);
        });
    });
}


/**
 * =========================================================================
 * MIGRATION GUIDE: How to integrate this into existing client code
 * =========================================================================
 * 
 * FILE: src/pages/Settings.js
 * 
 * BEFORE (current implementation):
 * ```javascript
 * const handleSubmit = async (e) => {
 *     e.preventDefault();
 *     const runId = Math.random().toString(36).substring(2, 15);
 *     
 *     // Create run
 *     await axios.post('/create_run', { parameters, run_id: runId });
 *     
 *     // Client orchestrates all steps
 *     await handleAnnotationRun(runId, user, updateAnnotation);
 * };
 * ```
 * 
 * AFTER (new implementation):
 * ```javascript
 * const handleSubmit = async (e) => {
 *     e.preventDefault();
 *     const runId = Math.random().toString(36).substring(2, 15);
 *     
 *     // Create run
 *     await axios.post('/create_run', { parameters, run_id: runId });
 *     
 *     // Use new architecture: server handles orchestration, client polls
 *     await handleAnnotationRunNewArchitecture(runId, user, updateAnnotation);
 * };
 * ```
 * 
 * The rest of the Settings.js file stays the same!
 * Just replace the handleAnnotationRun call with handleAnnotationRunNewArchitecture.
 * 
 * =========================================================================
 * 
 * REMOVED FILES (can be deleted after migration):
 * - src/utils/AnnotationRun.js (the old orchestrator)
 * - Most of the complex logic in src/utils/pipelinePolling.js
 * 
 * KEPT ROUTES (backward compatibility - old routes still work):
 * - /run_fastp_async, /run_megahit_async, etc. (old step-by-step API)
 * - These can coexist with /run_annotation_orchestrated
 * 
 * NEW ROUTE (use this going forward):
 * - POST /run_annotation_orchestrated - start complete pipeline
 * - GET /get_run?run_id=XXX - poll current status
 * 
 * =========================================================================
 */


/**
 * Example usage in a React component:
 * 
 * ```javascript
 * import { handleAnnotationRunNewArchitecture } from './AnnotationRunNew';
 * 
 * function Settings() {
 *     const handleSubmit = async () => {
 *         const runId = generateRunId();
 *         
 *         // Create empty run
 *         await axios.post('/create_run', { 
 *             parameters: allParameters,
 *             run_id: runId 
 *         });
 *         
 *         // Launch orchestration and poll for completion
 *         await handleAnnotationRunNewArchitecture(runId, currentUser, updateUICallback);
 *         
 *         // Now show completion message or redirect
 *     };
 *     
 *     return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
